// FilterBar — sort & filter for collection-style product grids
//
// Pure client-side: takes an array of products and exposes the filtered subset
// via the `onChange` callback. We intentionally do NOT push state to the URL
// (yet) — the catalogue is static enough that quick in-page tweaks beat a
// router round-trip.
//
// Filters:
//   - sort:    featured | newest | priceAsc | priceDesc | titleAsc
//   - inStock toggle
//   - price:   free min/max range (slider + numeric inputs + quick suggestions)
//   - size band: reuses SIZE_BUCKETS from /lib/sizes — only shown when at
//                least one product in the set has a parseable diameter.

import { useEffect, useMemo, useRef, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import type { ShopifyProduct } from "@/lib/shopify";
import { SIZE_BUCKETS, parseDiameterCm } from "@/lib/sizes";
import { useT } from "@/lib/i18n";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type SortKey = "featured" | "newest" | "priceAsc" | "priceDesc" | "titleAsc";

const PRICE_STEP = 50; // kr — granularity of the slider

function roundDown(n: number, step: number) {
  return Math.floor(n / step) * step;
}
function roundUp(n: number, step: number) {
  return Math.ceil(n / step) * step;
}
function fmt(n: number) {
  return n.toLocaleString("da-DK");
}

interface Props {
  products: ShopifyProduct[];
  onChange: (filtered: ShopifyProduct[]) => void;
  className?: string;
}

export function FilterBar({ products, onChange, className }: Props) {
  const { t, tn } = useT();
  const [sort, setSort] = useState<SortKey>("featured");
  const [inStock, setInStock] = useState(false);
  // null = no price filter active; tuple = [min, max] inclusive (kr)
  const [priceRange, setPriceRange] = useState<[number, number] | null>(null);
  const [sizeSlug, setSizeSlug] = useState<string | null>(null);

  // Compute catalog bounds from products (rounded to PRICE_STEP).
  const bounds = useMemo(() => {
    const vals = products
      .map((p) => parseFloat(p.node.priceRange.minVariantPrice.amount))
      .filter((n) => !Number.isNaN(n));
    if (vals.length === 0) return { min: 0, max: 1000 };
    const lo = roundDown(Math.min(...vals), PRICE_STEP);
    const hi = roundUp(Math.max(...vals), PRICE_STEP);
    return { min: lo, max: hi === lo ? lo + PRICE_STEP : hi };
  }, [products]);

  // Pre-compute size availability so we can hide the size facet entirely on
  // collections with no measurable items.
  const hasSizes = useMemo(
    () =>
      products.some(
        (p) =>
          parseDiameterCm({
            title: p.node.title,
            description: p.node.description,
            handle: p.node.handle,
          }) != null,
      ),
    [products],
  );

  const filtered = useMemo(() => {
    let out = products.slice();

    if (inStock) out = out.filter((p) => p.node.availableForSale);

    if (priceRange) {
      const [lo, hi] = priceRange;
      out = out.filter((p) => {
        const v = parseFloat(p.node.priceRange.minVariantPrice.amount);
        return v >= lo && v <= hi;
      });
    }

    if (sizeSlug) {
      const bucket = SIZE_BUCKETS.find((b) => b.slug === sizeSlug);
      if (bucket) {
        out = out.filter((p) => {
          const d = parseDiameterCm({
            title: p.node.title,
            description: p.node.description,
            handle: p.node.handle,
          });
          return d != null && d >= bucket.min && d < bucket.max;
        });
      }
    }

    const byDate = (p: ShopifyProduct) => {
      const ts = p.node.publishedAt ?? p.node.createdAt;
      return ts ? Date.parse(ts) : 0;
    };
    const byPrice = (p: ShopifyProduct) =>
      parseFloat(p.node.priceRange.minVariantPrice.amount) || 0;

    switch (sort) {
      case "newest":
        out.sort((a, b) => byDate(b) - byDate(a));
        break;
      case "priceAsc":
        out.sort((a, b) => byPrice(a) - byPrice(b));
        break;
      case "priceDesc":
        out.sort((a, b) => byPrice(b) - byPrice(a));
        break;
      case "titleAsc":
        out.sort((a, b) =>
          a.node.title.localeCompare(b.node.title, "da", { sensitivity: "base" }),
        );
        break;
      // "featured" leaves Shopify's order untouched
    }

    return out;
  }, [products, sort, inStock, priceRange, sizeSlug]);

  // Push the result up.
  useEffect(() => {
    onChange(filtered);
  }, [filtered, onChange]);

  const activeCount =
    (inStock ? 1 : 0) +
    (priceRange ? 1 : 0) +
    (sizeSlug ? 1 : 0) +
    (sort !== "featured" ? 1 : 0);

  const reset = () => {
    setSort("featured");
    setInStock(false);
    setPriceRange(null);
    setSizeSlug(null);
  };

  // Mobile sheet open state — desktop uses the inline bar.
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!sheetOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [sheetOpen]);

  // Label shown on the price chip / mobile section header.
  const priceChipLabel = priceRange
    ? `${fmt(priceRange[0])}–${fmt(priceRange[1])} kr`
    : t("filter.priceAny");

  return (
    <>
      {/* === Mobile trigger row (sticky) === */}
      <div
        className={`sticky top-[60px] z-30 -mx-6 mb-6 flex items-center justify-between border-y border-border bg-background/95 px-6 py-2.5 backdrop-blur md:hidden ${className ?? ""}`}
      >
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-foreground/30 px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-foreground"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {t("filter.title")}
          {activeCount > 0 && (
            <span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-terracotta px-1 text-[10px] font-medium text-primary-foreground">
              {activeCount}
            </span>
          )}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {tn("filter.results", filtered.length)}
        </span>
      </div>

      {/* === Mobile bottom sheet === */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-end bg-foreground/40 backdrop-blur-sm md:hidden"
          onClick={() => setSheetOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t("filter.title")}
        >
          <div
            className="max-h-[85vh] w-full overflow-y-auto rounded-t-xl bg-background p-5 shadow-2xl animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted" aria-hidden />
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl text-foreground">
                {t("filter.title")}
              </h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t("search.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Sort */}
              <div>
                <p className="mb-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("filter.sort")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "featured", label: t("filter.sort.featured") },
                    { value: "newest", label: t("filter.sort.newest") },
                    { value: "priceAsc", label: t("filter.sort.priceAsc") },
                    { value: "priceDesc", label: t("filter.sort.priceDesc") },
                    { value: "titleAsc", label: t("filter.sort.titleAsc") },
                  ].map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setSort(o.value as SortKey)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                        sort === o.value
                          ? "border-foreground bg-foreground text-background"
                          : "border-border text-foreground/80"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stock */}
              <div>
                <p className="mb-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("filter.availability")}
                </p>
                <button
                  onClick={() => setInStock((v) => !v)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    inStock
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-foreground/80"
                  }`}
                  aria-pressed={inStock}
                >
                  {t("filter.inStock")}
                </button>
              </div>

              {/* Price (slider + inputs + suggestions) */}
              <div>
                <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("filter.price")}
                </p>
                <PriceRangeControl
                  bounds={bounds}
                  value={priceRange}
                  onChange={setPriceRange}
                />
              </div>

              {/* Size */}
              {hasSizes && (
                <div>
                  <p className="mb-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {t("filter.size")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[{ value: "", label: t("filter.sizeAny") }, ...SIZE_BUCKETS.map((b) => ({ value: b.slug, label: b.labelDa.replace(/^.*?\s/, "") }))].map((o) => (
                      <button
                        key={o.value || "any"}
                        onClick={() => setSizeSlug(o.value || null)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                          (sizeSlug ?? "") === o.value
                            ? "border-foreground bg-foreground text-background"
                            : "border-border text-foreground/80"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky footer actions */}
            <div className="mt-6 flex items-center gap-3 border-t border-border pt-4">
              <button
                onClick={reset}
                className="rounded-full border border-border px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-foreground/70 hover:text-foreground"
              >
                {t("filter.reset")}
              </button>
              <button
                onClick={() => setSheetOpen(false)}
                className="flex-1 rounded-full bg-foreground py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-background hover:bg-terracotta"
              >
                {tn("filter.results", filtered.length)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Desktop inline bar === */}
      <div
        className={`sticky top-[64px] z-30 -mx-6 mb-10 hidden border-y border-border bg-background/95 px-6 py-3 backdrop-blur md:block md:-mx-10 md:px-10 ${className ?? ""}`}
      >
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <span className="mr-1 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {t("filter.title")}
          </span>

          <SelectChip
            label={t("filter.sort")}
            value={sort}
            onChange={(v) => setSort(v as SortKey)}
            options={[
              { value: "featured", label: t("filter.sort.featured") },
              { value: "newest", label: t("filter.sort.newest") },
              { value: "priceAsc", label: t("filter.sort.priceAsc") },
              { value: "priceDesc", label: t("filter.sort.priceDesc") },
              { value: "titleAsc", label: t("filter.sort.titleAsc") },
            ]}
          />

          <button
            onClick={() => setInStock((v) => !v)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              inStock
                ? "border-foreground bg-foreground text-background"
                : "border-border text-foreground/80 hover:border-foreground"
            }`}
            aria-pressed={inStock}
          >
            {t("filter.inStock")}
          </button>

          {/* Price popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  priceRange
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-foreground/80 hover:border-foreground"
                }`}
                aria-label={t("filter.price")}
              >
                <span className="opacity-70">{t("filter.price")}:</span>
                <span className="font-medium">{priceChipLabel}</span>
                <ChevronDown className="ml-0.5 h-3 w-3 opacity-70" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-4" align="start">
              <PriceRangeControl
                bounds={bounds}
                value={priceRange}
                onChange={setPriceRange}
              />
            </PopoverContent>
          </Popover>

          {hasSizes && (
            <SelectChip
              label={t("filter.size")}
              value={sizeSlug ?? ""}
              onChange={(v) => setSizeSlug(v || null)}
              options={[
                { value: "", label: t("filter.sizeAny") },
                ...SIZE_BUCKETS.map((b) => ({
                  value: b.slug,
                  label: `${b.labelDa.replace(/^.*?\s/, "")}`,
                })),
              ]}
            />
          )}

          <span className="ml-auto text-xs text-muted-foreground">
            {tn("filter.results", filtered.length)}
          </span>

          {activeCount > 0 && (
            <button
              onClick={reset}
              className="inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-terracotta"
            >
              <X className="h-3 w-3" />
              {t("filter.reset")}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

interface SelectChipProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

// Native <select> styled as a chip — keyboard- and screen-reader-friendly,
// no heavy dropdown machinery for a 5-option list.
function SelectChip({ label, value, options, onChange }: SelectChipProps) {
  const isDefault = value === "" || value === options[0].value;
  return (
    <label
      className={`relative inline-flex cursor-pointer items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition-colors ${
        isDefault
          ? "border-border text-foreground/80 hover:border-foreground"
          : "border-foreground bg-foreground text-background"
      }`}
    >
      <span className="opacity-70">{label}:</span>
      <span className="font-medium">
        {options.find((o) => o.value === value)?.label ?? options[0].label}
      </span>
      <ChevronDown className="ml-0.5 h-3 w-3 opacity-70" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label={label}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---- Price range control: slider + min/max inputs + quick suggestions ----

interface PriceRangeControlProps {
  bounds: { min: number; max: number };
  value: [number, number] | null;
  onChange: (next: [number, number] | null) => void;
}

function PriceRangeControl({ bounds, value, onChange }: PriceRangeControlProps) {
  const { t } = useT();
  const current: [number, number] = value ?? [bounds.min, bounds.max];

  // Mirror state as strings so users can type freely (incl. clearing the field).
  const [minStr, setMinStr] = useState<string>(String(current[0]));
  const [maxStr, setMaxStr] = useState<string>(String(current[1]));

  // Keep inputs in sync if external value changes (e.g. reset button).
  const lastValRef = useRef<string>("");
  const sig = `${current[0]}:${current[1]}:${value === null}`;
  useEffect(() => {
    if (sig === lastValRef.current) return;
    lastValRef.current = sig;
    setMinStr(String(current[0]));
    setMaxStr(String(current[1]));
  }, [sig, current]);

  const clamp = (n: number) => Math.max(bounds.min, Math.min(bounds.max, n));

  const commitFromStrings = (rawMin: string, rawMax: string) => {
    const parsedMin = rawMin.trim() === "" ? bounds.min : parseInt(rawMin, 10);
    const parsedMax = rawMax.trim() === "" ? bounds.max : parseInt(rawMax, 10);
    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax)) return;
    let lo = clamp(parsedMin);
    let hi = clamp(parsedMax);
    if (lo > hi) [lo, hi] = [hi, lo];
    // Round to step
    lo = roundDown(lo, PRICE_STEP);
    hi = roundUp(hi, PRICE_STEP);
    if (lo === bounds.min && hi === bounds.max) {
      onChange(null);
    } else {
      onChange([lo, hi]);
    }
  };

  const onSliderChange = (vals: number[]) => {
    if (vals.length < 2) return;
    const [lo, hi] = vals;
    setMinStr(String(lo));
    setMaxStr(String(hi));
    if (lo === bounds.min && hi === bounds.max) {
      onChange(null);
    } else {
      onChange([lo, hi] as [number, number]);
    }
  };

  // Build up to 3 sensible suggestions that fall within bounds.
  const suggestions = useMemo(() => {
    const out: Array<{ label: string; range: [number, number] }> = [];
    const add = (lo: number, hi: number, label: string) => {
      if (hi <= bounds.min || lo >= bounds.max) return;
      out.push({
        label,
        range: [Math.max(lo, bounds.min), Math.min(hi, bounds.max)] as [number, number],
      });
    };
    add(bounds.min, 500, t("filter.price.suggestUnder", { n: fmt(500) }));
    add(500, 1000, t("filter.price.suggestBetween", { a: fmt(500), b: fmt(1000) }));
    add(1000, 2500, t("filter.price.suggestBetween", { a: fmt(1000), b: fmt(2500) }));
    return out.slice(0, 3);
  }, [bounds.min, bounds.max, t]);

  return (
    <div className="space-y-4">
      {/* Slider */}
      <div className="px-1.5 pt-2">
        <SliderPrimitive.Root
          className="relative flex w-full touch-none select-none items-center"
          min={bounds.min}
          max={bounds.max}
          step={PRICE_STEP}
          value={[current[0], current[1]]}
          onValueChange={onSliderChange}
          minStepsBetweenThumbs={1}
        >
          <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted">
            <SliderPrimitive.Range className="absolute h-full bg-terracotta" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            aria-label={t("filter.price.min")}
            className="block h-4 w-4 rounded-full border border-foreground/40 bg-background shadow transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
          />
          <SliderPrimitive.Thumb
            aria-label={t("filter.price.max")}
            className="block h-4 w-4 rounded-full border border-foreground/40 bg-background shadow transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
          />
        </SliderPrimitive.Root>
        <div className="mt-1 flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>{fmt(bounds.min)} kr</span>
          <span>{fmt(bounds.max)} kr</span>
        </div>
      </div>

      {/* Min / Max inputs */}
      <div className="flex items-end gap-2">
        <label className="flex-1">
          <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("filter.price.min")}
          </span>
          <div className="mt-1 flex items-center rounded-md border border-border bg-background focus-within:border-foreground">
            <input
              type="number"
              inputMode="numeric"
              min={bounds.min}
              max={bounds.max}
              step={PRICE_STEP}
              value={minStr}
              onChange={(e) => setMinStr(e.target.value)}
              onBlur={() => commitFromStrings(minStr, maxStr)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-full bg-transparent px-2.5 py-1.5 text-sm tabular-nums outline-none"
              aria-label={t("filter.price.min")}
            />
            <span className="px-2 text-xs text-muted-foreground">kr</span>
          </div>
        </label>
        <span className="pb-2 text-muted-foreground">–</span>
        <label className="flex-1">
          <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("filter.price.max")}
          </span>
          <div className="mt-1 flex items-center rounded-md border border-border bg-background focus-within:border-foreground">
            <input
              type="number"
              inputMode="numeric"
              min={bounds.min}
              max={bounds.max}
              step={PRICE_STEP}
              value={maxStr}
              onChange={(e) => setMaxStr(e.target.value)}
              onBlur={() => commitFromStrings(minStr, maxStr)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-full bg-transparent px-2.5 py-1.5 text-sm tabular-nums outline-none"
              aria-label={t("filter.price.max")}
            />
            <span className="px-2 text-xs text-muted-foreground">kr</span>
          </div>
        </label>
      </div>

      {/* Quick suggestions */}
      {suggestions.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("filter.price.suggestions")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => {
              const active =
                value !== null && value[0] === s.range[0] && value[1] === s.range[1];
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => onChange(s.range)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                    active
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-foreground/80 hover:border-foreground"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
            {value !== null && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="rounded-full border border-transparent px-2 py-1 text-[11px] text-muted-foreground hover:text-terracotta"
              >
                {t("filter.reset")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
